import React, { useState } from 'react'
import { Modal, Carousel } from 'react-bootstrap'
import { FaImage, FaFilePdf, FaFileAlt, FaLink, FaDownload } from 'react-icons/fa'

import { downloadImage } from '../../../utils/download'
import { vehicleDisplayName } from '../../../utils/vehicleDisplayName'

// Shared insight helpers + presentational components used by both the ADAS
// section (AdasSections.js) and the read-only Mechanical section
// (MechanicalSection.js). Extracted verbatim from AdasSections.js so the two
// sections render insights identically; AdasSections behavior is unchanged.

export const ADAS_SLOT_OEM_POSITION = 'oem-position'
export const ADAS_SLOT_OEM_PROCEDURE = 'oem-procedure'
export const ADAS_SLOT_ADDITIONAL_MEDIA = 'additional-media'

// Apps attribute an added photo/doc to a section by the section TITLE in the
// item's `name` and send it slot-less (photos always; docs added via the
// full-object PUT). Recover the section from the name so existing app media
// buckets into the right tile without waiting for a re-save. The API stamps
// the same slot on new saves; this is the read-side mirror (parity with admin).
const ADAS_SECTION_TITLE_TO_SLOT = {
  'oem position statement': ADAS_SLOT_OEM_POSITION,
  'oem procedure': ADAS_SLOT_OEM_PROCEDURE,
  'additional media': ADAS_SLOT_ADDITIONAL_MEDIA,
}
export const adasSectionSlot = (item) => {
  if (item && item.slot) return item.slot
  const key = String((item && item.name) || '').trim().toLowerCase()
  return ADAS_SECTION_TITLE_TO_SLOT[key] || null
}

// The ServiceModule stores this sensor as the singular "Diagnostic Scan", but
// the approved designs show it pluralized ("Diagnostic Scans") in both the
// section header and the "… Insights" toggle. The DB value is shared widely and
// must not change, so pluralize at render time only.
const SENSOR_DISPLAY_NAMES = {
  'Diagnostic Scan': 'Diagnostic Scans',
}
export const displaySensorName = (name) => (name && SENSOR_DISPLAY_NAMES[name]) || name

export const formatPrice = (amount) => {
  return `$ ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export const getDomainTag = (file, slot) => {
  const raw = file && file.fileURL
  if (raw) {
    // Prepend a scheme so schemeless links ("oem1stop.com") still parse — a bare
    // URL would otherwise throw in new URL() and lose its tag (Finding 26).
    const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : 'https://' + raw
    try {
      const hostname = new URL(normalized).hostname
      if (hostname.includes('oem1stop.com')) return 'OEM1Stop'
      if (hostname.includes('alldata.com')) return 'ALLDATA'
      if (hostname.includes('repairlogic.com')) return 'RepairLogic'
    } catch (e) {
      // invalid URL
    }
  }
  // Generic OEM tag for OEM-documentation slots — adasThink-generated OR manually
  // added — so every OEM link is labeled even when the domain isn't recognized.
  const isOemSlot = slot === ADAS_SLOT_OEM_POSITION || slot === ADAS_SLOT_OEM_PROCEDURE
  if (isOemSlot || (file && file.source === 'adasThink')) return 'OEM'
  return null
}

export const getSensorInsight = (services, sensorInsights) => {
  for (const svc of services) {
    if (svc.rawSensorId) {
      const match = (sensorInsights || []).find(si => si.sensorId === svc.rawSensorId)
      if (match) return match
    }
  }
  return null
}

export const getSensorDocuments = (report, services) => {
  const documents = report.documents || []
  const sensorSlugs = services.map(svc => svc.sensorId).filter(Boolean)
  return documents.filter((doc) => {
    if (doc.service !== 'adas') return false
    if (doc.sensorSlug) return sensorSlugs.includes(doc.sensorSlug)
    return sensorSlugs.includes(doc.sensorId)
  })
}

export const getSensorPhotos = (report, services) => {
  const photos = report.photos || []
  const sensorSlugs = services.map(svc => svc.sensorId).filter(Boolean)
  return photos.filter(photo => (
    photo.service === 'adas' &&
    photo.slot !== 'adas-document' &&
    sensorSlugs.includes(photo.sensorId)
  ))
}

export const getLinkedSafetySystems = (report, services) => {
  const safetyFeatures = report.safetyFeatures || []
  const sensorKeys = {}

  // feature.linkedSensorIds are raw adasThink UUIDs — match on rawSensorId
  // only. Two sensors can collapse into one subcategory, so matching the slug
  // sensorId would cross-link equipped systems into the wrong subcategory.
  services.forEach((svc) => {
    if (svc.rawSensorId) sensorKeys[String(svc.rawSensorId)] = true
  })

  const systems = []
  const seen = {}
  safetyFeatures.forEach((feature) => {
    if (!feature || feature.answer !== 'equipped') return
    const linked = feature.linkedSensorIds || []
    if (!linked.length) return
    const isLinked = linked.some(id => sensorKeys[String(id)])
    if (isLinked && feature.name && !seen[feature.name]) {
      seen[feature.name] = true
      systems.push({ name: feature.name, oemName: feature.oemName, sourceDetails: feature.sourceDetails })
    }
  })

  return systems
}

const getConditionVariant = (status) => {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'repair') return 'repair'
  if (normalized === 'replace') return 'replace'
  return 'install'
}

const getConditionLabel = (status) => {
  if (status === 'Install') return 'Installed'
  if (status === 'Repair') return 'Repaired'
  if (status === 'Replace') return 'Replaced'
  return status || 'Unknown'
}

// Classify an attachment by extension so we can show the right icon (F25) and
// decide open behavior. Mirrors the admin portal's getAttachmentType.
const getRowType = (url) => {
  const clean = String(url || '').split('?')[0].toLowerCase()
  if (/\.(png|jpe?g|gif|webp|bmp|heic|svg)$/.test(clean)) return 'image'
  if (/\.pdf$/.test(clean)) return 'pdf'
  if (/\.(docx?|xlsx?|pptx?|txt|csv)$/.test(clean)) return 'file'
  if (/^https?:\/\//.test(String(url || ''))) return 'link'
  return 'file'
}

const RowIcon = ({ url }) => {
  const type = getRowType(url)
  if (type === 'image') return <FaImage className='adas-detail__doc-file-icon' />
  if (type === 'pdf') return <FaFilePdf className='adas-detail__doc-file-icon' />
  if (type === 'link') return <FaLink className='adas-detail__doc-file-icon' />
  return <FaFileAlt className='adas-detail__doc-file-icon' />
}

// A row with an `onClick` opens the in-page photo viewer (F24); otherwise it is
// a link that opens the file in a new tab. Photos and documents can therefore
// coexist in one tile (app parity: OEM sections show their photos too). Every
// row shows a type icon (F25).
export const DocumentationTile = ({ title, rows, emptyMessage, showDomainTag = true }) => {
  return (
    <div className='adas-detail__doc-tile'>
      <div className='adas-detail__doc-tile-header'>
        <span className='adas-detail__doc-tile-title'>{title}</span>
      </div>
      <div className='adas-detail__doc-tile-files'>
        {rows.length ? rows.map((row, idx) => (
          <div key={`${title}-${idx}`} className='adas-detail__doc-file-row'>
            {row.onClick ? (
              <button
                type='button'
                className='adas-detail__doc-file-link adas-detail__doc-file-btn'
                onClick={row.onClick}
              >
                <RowIcon url={row.url} />
                <span className='adas-detail__doc-file-name'>{row.name}</span>
              </button>
            ) : (
              <a
                href={row.url}
                target='_blank'
                rel='noopener noreferrer'
                className='adas-detail__doc-file-link'
              >
                <RowIcon url={row.url} />
                <span className='adas-detail__doc-file-name'>{row.name}</span>
              </a>
            )}
            {showDomainTag && row.tag && (
              <span className='adas-detail__doc-domain-tag'>{row.tag}</span>
            )}
          </div>
        )) : (
          <span className='adas-detail__doc-tile-empty'>{emptyMessage}</span>
        )}
      </div>
    </div>
  )
}

// Hover text for a safety system imported from an ADAS Map (OPUS) report: the rows
// of that report's "Identified ADAS Systems" table it came from, as printed.
// undefined (no tooltip) for systems from anywhere else.
const sourceDetailsTitle = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return undefined
  return rows
    .map(row => `System: ${row.system || '—'}\nComponent: ${row.component || '—'}\nLocation: ${row.location || '—'}`)
    .join('\n\n')
}

export const StatusBadge = ({ status, source }) => {
  if (!status) return null

  if (source === 'manual' && status !== 'removed') {
    return (
      <span className='adas-detail__status-badge custom'>
        Custom
      </span>
    )
  }

  let label = 'Potential'
  let variant = 'potential'

  if (status === 'required') {
    label = 'Required'
    variant = 'required'
  } else if (status === 'removed') {
    label = 'Removed'
    variant = 'removed'
  }

  return (
    <span className={`adas-detail__status-badge ${variant}`}>
      {label}
    </span>
  )
}

// OE Service Information as stored: headings and paragraphs one per line, "• "
// bullets, a blank line before each new group. Bullets become a list so a wrapped
// bullet hangs under its own text (admin parity: AdasReportDetail.renderOeInformation).
const OeInformation = ({ text }) => {
  const blocks = []
  let newGroup = false
  String(text).split('\n').forEach(line => {
    if (!line.trim()) {
      newGroup = true
      return
    }
    const last = blocks[blocks.length - 1]
    if (line.startsWith('• ') && last && last.items && !newGroup) {
      last.items.push(line.slice(2))
    } else if (line.startsWith('• ')) {
      blocks.push({ items: [line.slice(2)], newGroup })
    } else {
      blocks.push({ text: line, newGroup })
    }
    newGroup = false
  })
  return (
    <div className='adas-detail__oe-info'>
      {blocks.map((block, idx) => {
        const className = block.newGroup ? 'is-new-group' : undefined
        if (!block.items) return <p key={`p-${idx}`} className={className}>{block.text}</p>
        return (
          <ul key={`ul-${idx}`} className={className}>
            {block.items.map(item => <li key={item}>{item}</li>)}
          </ul>
        )
      })}
    </div>
  )
}

// An ADAS Map import prints its own reason for each calibration — Location,
// Trigger and OE Service Information — and that replaces the generic "Why These
// Services are Required" (admin parity: AdasReportDetail.renderAdasMapInsight).
const AdasMapInsight = ({ entries }) => (
  <>
    {entries.map((entry, idx) => (
      <div key={`${entry.component}-${idx}`} className='adas-detail__adas-map-entry'>
        {entries.length > 1 && entry.component && (
          <h4 className='adas-detail__sensor-insights-title'>{entry.component}</h4>
        )}
        {entry.location && (
          <p className='adas-detail__adas-map-field'>
            <span className='adas-detail__adas-map-label'>Location:</span> {entry.location}
          </p>
        )}
        {entry.trigger && (
          <p className='adas-detail__adas-map-field'>
            <span className='adas-detail__adas-map-label'>Trigger:</span> {entry.trigger}
          </p>
        )}
        {entry.oeServiceInformation && (
          <>
            <h4 className='adas-detail__sensor-insights-title adas-detail__oe-info-title'>
              OE Service Information
            </h4>
            <OeInformation text={entry.oeServiceInformation} />
          </>
        )}
      </div>
    ))}
  </>
)

const SensorInsights = ({ sensorName, services, sensorInsights, report, collision, open, onToggle, includeCustom }) => {
  // Open state is CONTROLLED by the parent (AdasSections / MechanicalSection).
  //
  // It used to live here, seeded from a `forceOpen` + `expandNonce` broadcast.
  // That kept "expand all by default" working (#37249) while still letting a
  // user collapse one section — the broadcast was a default, not a lock — but it
  // made the state invisible to the parent, and the section header now needs it:
  // the header only pins while its own section is expanded, and it is a SIBLING
  // of this component, so it cannot read state held in here.
  //
  // The parents keep the default-not-lock semantics; they just own the map now.
  const [lightbox, setLightbox] = useState({ open: false, index: 0 })

  const insight = getSensorInsight(services, sensorInsights)
  const hasInsights = !!insight
  const adasMapEntries = (insight && insight.adasMap) || []
  const isManualGroup = services.some(svc => svc.recommendedSource === 'manual')
  // AdasSections never passes includeCustom, so showInsights reduces to the
  // original (hasInsights || isManualGroup) there. The Mechanical section opts
  // in via includeCustom so a custom-only group still renders the (blank)
  // Additional Documentation block.
  const isCustomGroup = includeCustom && services.some(svc => svc.custom)
  const showInsights = hasInsights || isManualGroup || isCustomGroup

  if (!showInsights) return null

  const isOpen = !!open
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'
  // Admin decides the collision justification per sensor group (services.some(collisionRelated)),
  // and hides collision services entirely when the vehicle wasn't in a collision. Mirror that net
  // outcome: only show the collision text when the report is a collision AND this group actually
  // contains a collision-triggered service — otherwise fall back to the repair-manual conditions.
  const isCollisionGroup = !!collision && services.some(svc => svc.collisionRelated)

  const sensorDocs = getSensorDocuments(report, services)
  // Bucket by EFFECTIVE slot (stored slot, else derived from the section title
  // in the name) so app-added media lands in the section the user chose — for
  // documents AND photos.
  const matchSlot = slot => sensorDocs.filter(doc => adasSectionSlot(doc) === slot)
  const positionDocs = matchSlot(ADAS_SLOT_OEM_POSITION)
  const procedureDocs = matchSlot(ADAS_SLOT_OEM_PROCEDURE)
  // Additional Media catches its own slot PLUS any slot-less / unknown-slot docs
  // (e.g. links/files added from the mobile app, which carry no slot) so they are
  // never silently dropped — parity with the admin portal (QA #23).
  const mediaDocs = sensorDocs.filter(doc => {
    const s = adasSectionSlot(doc)
    return (
      s === ADAS_SLOT_ADDITIONAL_MEDIA ||
      (s !== ADAS_SLOT_OEM_POSITION && s !== ADAS_SLOT_OEM_PROCEDURE)
    )
  })

  const mapDocRows = docs => docs.flatMap((doc) => (
    (doc.files || []).map((file) => ({
      url: file.fileURL,
      name: file.note || (file.fileURL ? file.fileURL.split('/').pop() : '') || doc.name,
      tag: getDomainTag(file, adasSectionSlot(doc)),
    }))
  )).filter(row => !!row.url)

  const positionRows = mapDocRows(positionDocs)
  const procedureRows = mapDocRows(procedureDocs)
  const mediaRows = mapDocRows(mediaDocs)

  // Photos are attributed to a section the same way; anything not in an
  // OEM/Additional section stays in the sensor Photos tile. Every photo gets a
  // GLOBAL index into `allPhotoRows` so the single lightbox pages through all of
  // this sensor's photos regardless of which tile they render in.
  const allSensorPhotos = getSensorPhotos(report, services)
  const photosForSlot = slot => allSensorPhotos.filter(p => adasSectionSlot(p) === slot)
  const positionPhotos = photosForSlot(ADAS_SLOT_OEM_POSITION)
  const procedurePhotos = photosForSlot(ADAS_SLOT_OEM_PROCEDURE)
  const mediaPhotos = photosForSlot(ADAS_SLOT_ADDITIONAL_MEDIA)
  const tilePhotos = allSensorPhotos.filter(p => {
    const s = adasSectionSlot(p)
    return (
      s !== ADAS_SLOT_OEM_POSITION &&
      s !== ADAS_SLOT_OEM_PROCEDURE &&
      s !== ADAS_SLOT_ADDITIONAL_MEDIA
    )
  })

  let photoIndex = 0
  const toPhotoRows = photos => photos
    .filter(photo => !!photo.url)
    .map((photo) => {
      const index = photoIndex++
      return {
        url: photo.url,
        // downloadUrl is attachment-signed by the portal API so the lightbox forces
        // a real file download instead of opening the image in a tab (QA #31).
        downloadUrl: photo.downloadUrl || photo.url,
        name: photo.name || photo.note || (photo.url ? photo.url.split('/').pop() : '') || 'Photo',
        onClick: () => setLightbox({ open: true, index }),
      }
    })
  // Order must match `allPhotoRows` below so each row's index is correct.
  const sensorPhotoRows = toPhotoRows(tilePhotos)
  const positionPhotoRows = toPhotoRows(positionPhotos)
  const procedurePhotoRows = toPhotoRows(procedurePhotos)
  const mediaPhotoRows = toPhotoRows(mediaPhotos)
  const allPhotoRows = [
    ...sensorPhotoRows,
    ...positionPhotoRows,
    ...procedurePhotoRows,
    ...mediaPhotoRows,
  ]

  const linkedSafetySystems = getLinkedSafetySystems(report, services)

  return (
    <div className='adas-detail__insights'>
      <button className='adas-detail__insights-toggle' onClick={() => onToggle && onToggle(!isOpen)}>
        <span>{displaySensorName(sensorName || 'Sensor')} Insights</span>
        <span className={`adas-detail__chevron ${isOpen ? 'adas-detail__chevron--expanded' : ''}`}>
          &#9662;
        </span>
      </button>
      {isOpen && (
        <div className='adas-detail__insights-content'>
          {hasInsights && adasMapEntries.length > 0 && (
            <div className='adas-detail__sensor-insights'>
              <AdasMapInsight entries={adasMapEntries} />
            </div>
          )}
          {hasInsights && !adasMapEntries.length && (
            <div className='adas-detail__sensor-insights'>
              <h4 className='adas-detail__sensor-insights-title'>
                Why These Services are Required
              </h4>
              {isCollisionGroup ? (
                <p className='adas-detail__insight-justification'>
                  Service(s) are required due to the vehicle being in {collisionPhrase}.
                </p>
              ) : (
                <>
                  <p className='adas-detail__insight-justification'>
                    Per the {vehicleDisplayName(report.vehicle)} repair
                    manual, the {sensorName} requires the above mentioned procedure(s) should any of the following occur:
                  </p>
                  <div className='adas-detail__conditions-grid'>
                    {(insight.conditions || []).map((condition, idx) => {
                      const conditionItem = condition.items && condition.items[0]
                      const status = conditionItem && conditionItem.status
                      const lineRefs = conditionItem && conditionItem.lineReferences
                      const variant = getConditionVariant(status)
                      const label = getConditionLabel(status)

                      return (
                        <div key={idx} className='adas-detail__condition-tile'>
                          <span className='adas-detail__condition-component'>
                            {condition.component}
                          </span>
                          <div className={`adas-detail__condition-tile-bottom adas-detail__condition-tile-bottom--${variant}`}>
                            <span className='adas-detail__condition-status'>
                              {label}
                            </span>
                            {lineRefs && (
                              <span className='adas-detail__condition-lines'>
                                Lines {lineRefs}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className='adas-detail__additional-docs'>
            <h4 className='adas-detail__additional-docs-title'>Additional Documentation</h4>
            <div className='adas-detail__doc-tiles-grid'>
              <DocumentationTile
                title='OEM Position Statement'
                rows={[...positionRows, ...positionPhotoRows]}
                emptyMessage='No documents to show'
              />
              <DocumentationTile
                title='OEM Procedure'
                rows={[...procedureRows, ...procedurePhotoRows]}
                emptyMessage='No documents to show'
              />
              <DocumentationTile
                title={`${displaySensorName(sensorName)} Photos`}
                rows={sensorPhotoRows}
                emptyMessage='No photos to show'
                showDomainTag={false}
              />
              <DocumentationTile
                title='Additional Media'
                rows={[...mediaRows, ...mediaPhotoRows]}
                emptyMessage='No media to show'
                showDomainTag={false}
              />
            </div>
          </div>

          {linkedSafetySystems.length > 0 && (
            <div className='adas-detail__safety-systems'>
              <h4 className='adas-detail__safety-systems-title'>
                Equipped Safety Systems
              </h4>
              <p className='adas-detail__safety-systems-subtitle'>
                The {sensorName} is responsible for the following safety systems:
              </p>
              <div className='adas-detail__safety-systems-grid'>
                {linkedSafetySystems.map(system => (
                  <div
                    key={system.name}
                    className='adas-detail__safety-system-card'
                    title={sourceDetailsTitle(system.sourceDetails)}
                  >
                    <span className='adas-detail__safety-system-name'>
                      {system.name}
                    </span>
                    {system.oemName && system.oemName !== system.name && (
                      <span className='adas-detail__safety-system-detail'>
                        ({system.oemName})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {lightbox.open && allPhotoRows.length > 0 && (
        <Modal
          show
          onHide={() => setLightbox({ open: false, index: 0 })}
          centered
          size='lg'
          dialogClassName='adas-photo-lightbox'
        >
          <button
            className='adas-modal-close'
            onClick={() => setLightbox({ open: false, index: 0 })}
            aria-label='Close'
          >
            &times;
          </button>
          <Modal.Body className='adas-photo-lightbox__body'>
            <Carousel
              activeIndex={lightbox.index}
              onSelect={(i) => setLightbox(prev => ({ ...prev, index: i }))}
              interval={null}
              indicators={allPhotoRows.length > 1}
              controls={allPhotoRows.length > 1}
            >
              {allPhotoRows.map((row, idx) => (
                <Carousel.Item key={`lb-${idx}`}>
                  <img src={row.url} alt={row.name || 'Photo'} className='adas-photo-lightbox__img' />
                  {row.name && (
                    <p className='adas-photo-lightbox__caption'>{row.name}</p>
                  )}
                </Carousel.Item>
              ))}
            </Carousel>
            <div className='adas-photo-lightbox__actions'>
              <button
                type='button'
                className='adas-photo-lightbox__download'
                onClick={() => {
                  // Download the item currently shown in the carousel, matching
                  // the admin portal's photo viewer (Finding 30).
                  const active = allPhotoRows[lightbox.index] || allPhotoRows[0]
                  if (active) downloadImage(active.downloadUrl || active.url, active.name || 'photo')
                }}
              >
                Download Photo
                <FaDownload className='adas-photo-lightbox__download-icon' />
              </button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  )
}

export default SensorInsights
